import { autoAnimatePlugin } from '@formkit/auto-animate/vue';
import messages from '@intlify/unplugin-vue-i18n/messages';
import Clarity from '@microsoft/clarity';
import { AppContextPlugin } from '@tundr/app-context';
import { AuthPlugin, useAuthStore } from '@tundr/auth';
import { LifecyclePhase, useLifecycleHooksStore } from '@tundr/lifecycle-hooks';
import { BreadcrumbPlugin, breadcrumbResetOp } from '@tundr/breadcrumb';
import { FeatureFlagPlugin } from '@tundr/feature-flags';
import { GeoPlugin } from '@tundr/geo';
import { HttpPlugin } from '@tundr/http';
import {
  createMissingTranslationHandler,
  generateI18nInstance,
  I18nPlugin,
  replaceLocaleMessages,
} from '@tundr/i18n';
import { modalResetOp } from '@tundr/modal';
import { notificationResetOp } from '@tundr/notification';
import { ServiceWorkerPlugin } from '@tundr/service-worker';
import { useToast } from '@tundr/toast';
import {
  companyDetailFetchOp,
  companyDetailResetOp,
} from './core/lifecycle-hooks/company.hooks.ts';
import {
  permissionsFetchOp,
  permissionsResetOp,
} from './core/lifecycle-hooks/permissions.hooks.ts';
import { useFiltersStore } from '@tundr/table';
import tundrFavicon from '@tundr/theme-assets/favicon/tundr-favicon.png';
import '@tundr/theme-globals';
import { ValidationPlugin } from '@tundr/validation';
import { useFavicon } from '@vueuse/core';
import { createPinia } from 'pinia';
import { createApp } from 'vue';
import { createRouter, createWebHashHistory } from 'vue-router';
import { version } from '../appVersion.json';
import App from './App.vue';
import { ENV_VARS, Environments, NodeEnvironments } from './core/config/env';
import {
  ExternalService,
  isServiceEnabled,
} from './core/config/external-services.ts';
import { useSupportChat } from './core/config/support/support.ts';
import { initAmplitude } from './core/config/tracking/amplitude.ts';
import {
  initSentry,
  registerStaleChunkReload,
  reportMissingTranslationToSentry,
  reportServiceWorkerEventToSentry,
  setSentryExtra,
  setSentryUser,
} from '@tundr/observability';
import { appRoutesDictionary } from './core/router/app-routes.dictionary.ts';
import { routes } from './core/router/routes';

const icon = useFavicon();

icon.value = tundrFavicon;

const pinia = createPinia();

const app = createApp(App);

// enables Vue-specific performance markers in Chrome DevTools' performance timeline.
if (ENV_VARS.node === NodeEnvironments.DEVELOPMENT) {
  app.config.performance = true;
}

app.use(AppContextPlugin);

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

/**
 * Before each route change, reset the filters in the filters store
 */
router.beforeEach((to, from) => {
  const filtersStore = useFiltersStore();

  const getBasePath = (path: string) => {
    const segments = path.split('/');
    return segments.length > 2 ? segments[2] : '';
  };
  const isBasePathChanging = getBasePath(to.path) !== getBasePath(from.path);

  if (isBasePathChanging) {
    filtersStore.resetAllFilters();
  }
});

app.use(pinia);

const authStore = useAuthStore();
const lifecycleHooks = useLifecycleHooksStore();

// Router installation starts the initial navigation, so route guards must see
// an attached lifecycle-hooks store before app.use(router).
lifecycleHooks.attachApp(app);
lifecycleHooks.register(notificationResetOp);
lifecycleHooks.register(breadcrumbResetOp);
lifecycleHooks.register(modalResetOp);
lifecycleHooks.register(permissionsFetchOp);
lifecycleHooks.register(permissionsResetOp);
lifecycleHooks.register(companyDetailFetchOp);
lifecycleHooks.register(companyDetailResetOp);

const i18n = generateI18nInstance({
  messages,
  debugMode: ENV_VARS.node === NodeEnvironments.DEVELOPMENT,
  missing: createMissingTranslationHandler({
    onMissing: reportMissingTranslationToSentry,
  }),
});

if (import.meta.hot) {
  import.meta.hot.accept('@intlify/unplugin-vue-i18n/messages', (module) => {
    if (module) {
      replaceLocaleMessages(i18n, module.default);
    }
  });
}

app.use(I18nPlugin, { i18n });

app.use(ValidationPlugin, { i18n: i18n.global });

app.provide('appVersion', version);

app.use(router);

app.use(BreadcrumbPlugin, { resetPath: appRoutesDictionary.root.app.path });

app.use(HttpPlugin, { baseURL: ENV_VARS.api.baseUrl });

app.use(AuthPlugin, { configureCompanyIdInHeaders: true });

app.use(FeatureFlagPlugin, {
  apiHost: ENV_VARS.growthbook.apiHost,
  clientKey: ENV_VARS.growthbook.clientKey,
  enableDevMode: ENV_VARS.environment === Environments.LOCAL,
  syncAttributesFromAuthStore: true,
});

app.use(GeoPlugin, {
  googleMapsApiKey: ENV_VARS.googleMaps.apiKey,
  googleMapId: ENV_VARS.googleMaps.mapId,
});

app.use(autoAnimatePlugin);

if (isServiceEnabled(ExternalService.Clarity) && ENV_VARS.clarity.projectId) {
  Clarity.init(ENV_VARS.clarity.projectId);
}

registerStaleChunkReload();

if (isServiceEnabled(ExternalService.Sentry) && ENV_VARS.sentry.dsn) {
  initSentry({
    app,
    router,
    dsn: ENV_VARS.sentry.dsn,
    environment: ENV_VARS.environment,
    release: version,
  });

  if (authStore.user) {
    setSentryUser(
      authStore.user?.id,
      `${authStore.user?.firstName} ${authStore.user?.lastName}`,
    );
  }
  if (authStore.selectedCompany) {
    setSentryExtra('companyId', authStore.selectedCompany?.id);
    setSentryExtra('companyName', authStore.selectedCompany?.name);
  }
}

// Registered after initSentry so the update-flow events reach the reporter.
app.use(ServiceWorkerPlugin, {
  router,
  onEvent: (event) => {
    reportServiceWorkerEventToSentry(event);
    if (event.type === 'reload-guard-exhausted') {
      useToast().showWarning({
        distinctId: 'sw-update-required',
        message: i18n.global.t('common.service_worker.update_required.title'),
        description: i18n.global.t(
          'common.service_worker.update_required.description',
        ),
      });
    }
  },
});

if (isServiceEnabled(ExternalService.Amplitude) && ENV_VARS.amplitude.apiKey) {
  const amplitudeTrackingPayload: {
    userId?: string;
  } = {};

  if (authStore.selectedCompany) {
    amplitudeTrackingPayload.userId = authStore.selectedCompany.hrId;
  }

  initAmplitude(amplitudeTrackingPayload, () => ({
    hasSession: !!authStore.authData,
    company: authStore.selectedCompany,
  }));
}

const supportChat = useSupportChat();
supportChat.initSupportChat();
supportChat.watchVerticalPadding();

await lifecycleHooks.run(LifecyclePhase.APP_LAUNCH);

app.mount('#app');
